import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, X, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Admin-only KB drafts approval UI. Ships every pending Dox-drafted (state, trade)
 * KB entry with inline edit → approve → live in the compliance engine.
 */
export default function KBDrafts() {
  const [drafts, setDrafts] = useState(null);
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/kb-drafts");
      setDrafts(data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required.");
      else toast.error("Failed to load drafts.");
      setDrafts([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (id, key, value) => setEditing((p) => ({ ...p, [id]: { ...(p[id] || {}), [key]: value } }));

  const approve = async (draft) => {
    setBusy((b) => ({ ...b, [draft.id]: true }));
    try {
      const edits = editing[draft.id] || {};
      await api.post("/admin/kb-drafts/approve", { draft_id: draft.id, edits });
      toast.success(`${draft.state} × ${draft.trade} promoted to live KB.`);
      await load();
    } catch { toast.error("Approve failed."); }
    finally { setBusy((b) => ({ ...b, [draft.id]: false })); }
  };

  const reject = async (draft) => {
    setBusy((b) => ({ ...b, [draft.id]: true }));
    try {
      await api.post("/admin/kb-drafts/reject", { draft_id: draft.id });
      toast.success(`Draft rejected.`);
      await load();
    } catch { toast.error("Reject failed."); }
    finally { setBusy((b) => ({ ...b, [draft.id]: false })); }
  };

  if (drafts === null) return <div className="min-h-screen bg-ink text-white flex items-center justify-center"><Loader2 size={32} className="animate-spin text-dox-orange" /></div>;

  return (
    <div className="min-h-screen bg-ink text-white dash-stage px-6 py-16">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-dox-orange" />
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-dox-magenta">Admin · Knowledge Base</div>
        </div>
        <h1 className="font-display font-black text-3xl mb-2">Dox-drafted KB entries</h1>
        <p className="text-white/60 text-sm mb-8 max-w-2xl">
          When Dox hits an unknown state × trade combo it drafts a KB entry with citations. Review, tweak, and approve.
          Approved rows go straight into the live compliance engine — no code deploy needed.
        </p>

        {drafts.length === 0 ? (
          <div className="glass-card p-10 text-center">
            <div className="text-white/85 font-semibold">Inbox zero.</div>
            <div className="text-white/45 text-sm mt-1">No drafts waiting for review.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {drafts.map((d) => <DraftCard key={d.id} draft={d} editing={editing[d.id] || {}} onPatch={(k, v) => patch(d.id, k, v)} onApprove={() => approve(d)} onReject={() => reject(d)} busy={!!busy[d.id]} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function DraftCard({ draft, editing, onPatch, onApprove, onReject, busy }) {
  const body = { ...(draft.draft || {}), ...editing };
  return (
    <div className="glass-card p-6" data-testid={`kb-draft-${draft.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-white/40">{new Date(draft.created_at).toLocaleString()}</div>
          <div className="font-semibold text-white text-lg mt-0.5">{draft.state} × {draft.trade}</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`kb-reject-${draft.id}`} onClick={onReject} disabled={busy} className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/75 hover:text-white hover:border-white/30 flex items-center gap-1.5 disabled:opacity-60">
            <X size={14} /> Reject
          </button>
          <button data-testid={`kb-approve-${draft.id}`} onClick={onApprove} disabled={busy} className="rounded-full bg-dox-orange px-5 py-2 text-sm font-bold text-white hover:bg-dox-orangeHover flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <Field label="License required" type="bool" value={body.license_required} onChange={(v) => onPatch("license_required", v)} />
        <Field label="Exam required" type="bool" value={body.exam_required} onChange={(v) => onPatch("exam_required", v)} />
        <Field label="Renewal cycle (mo)" type="number" value={body.renewal_cycle_months} onChange={(v) => onPatch("renewal_cycle_months", v)} />
        <Field label="Renewal fee (USD)" type="number" value={body.typical_renewal_fee_usd} onChange={(v) => onPatch("typical_renewal_fee_usd", v)} />
        <Field label="Bond (USD)" type="number" value={body.typical_bond_usd} onChange={(v) => onPatch("typical_bond_usd", v)} />
        <Field label="Experience (yrs)" type="number" value={body.typical_experience_years} onChange={(v) => onPatch("typical_experience_years", v)} />
        <Field label="Issuing agency" type="text" value={body.issuing_agency_name} onChange={(v) => onPatch("issuing_agency_name", v)} full />
        <Field label="Official link" type="text" value={body.official_link} onChange={(v) => onPatch("official_link", v)} full />
        <Field label="Notes" type="textarea" value={body.notes} onChange={(v) => onPatch("notes", v)} full />
      </div>

      {body.official_link && (
        <a href={body.official_link} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-dox-orange hover:text-dox-orangeHover">
          Open cited source <ExternalLink size={12} />
        </a>
      )}
    </div>
  );
}

function Field({ label, type, value, onChange, full }) {
  const wrap = `${full ? "sm:col-span-2" : ""}`;
  const base = "w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-dox-orange transition";
  if (type === "bool") {
    return (
      <label className={wrap + " flex items-center gap-3"}>
        <span className="text-xs tracking-widest uppercase text-white/40 flex-1">{label}</span>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-dox-orange" />
      </label>
    );
  }
  if (type === "textarea") {
    return (
      <label className={wrap + " block"}>
        <div className="text-xs tracking-widest uppercase text-white/40 mb-1.5">{label}</div>
        <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} />
      </label>
    );
  }
  return (
    <label className={wrap + " block"}>
      <div className="text-xs tracking-widest uppercase text-white/40 mb-1.5">{label}</div>
      <input type={type === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value ? Number(e.target.value) : null) : e.target.value)} className={base} />
    </label>
  );
}
